import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, X, Sparkles, Crown } from "lucide-react";

interface TrainerValueActivatorProps {
  trainerId: string;
  subscriptionType: string;
  onDismiss: () => void;
}

export const TrainerValueActivator = ({ 
  trainerId, 
  subscriptionType,
  onDismiss 
}: TrainerValueActivatorProps) => {
  const [show, setShow] = useState(false);
  const [stats, setStats] = useState({
    profileViews: 0,
    contactClicks: 0
  });

  useEffect(() => {
    // Check if has basic engagement
    const checkEngagement = () => {
      // For now, simulate some basic engagement
      // In production, you'd fetch from a trainer_contact_clicks or similar table
      const dismissedKey = `trainer-value-activator-dismissed-${trainerId}`;
      const isDismissed = localStorage.getItem(dismissedKey);

      // Simulate engagement (in production, get from database)
      const simulatedViews = Math.floor(Math.random() * 20) + 5;
      const simulatedContacts = Math.floor(Math.random() * 3) + 1;

      setStats({
        profileViews: simulatedViews,
        contactClicks: simulatedContacts
      });

      // Show if not premium/lifetime and has higher engagement threshold
      const hasEngagement = simulatedViews > 25 || simulatedContacts > 3;
      const isPremium = subscriptionType === 'yearly' || subscriptionType === 'lifetime';
      
      if (!isPremium && hasEngagement && !isDismissed) {
        setShow(true);
      }
    };

    checkEngagement();
  }, [trainerId, subscriptionType]);

  const handleDismiss = () => {
    localStorage.setItem(`trainer-value-activator-dismissed-${trainerId}`, 'true');
    setShow(false);
    onDismiss();
  };

  if (!show) {
    return null;
  }

  return (
    <Card className="border-primary bg-gradient-to-br from-primary/10 to-secondary/10 shadow-lg">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary rounded-full">
              <Sparkles className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h3 className="font-bold text-lg">¡Tu Perfil Está Funcionando! 🎉</h3>
              <p className="text-sm text-muted-foreground">
                Ya estás recibiendo interés de clientes
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleDismiss}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-background/50 backdrop-blur-sm rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-primary">{stats.profileViews}</div>
            <div className="text-xs text-muted-foreground">Vistas</div>
          </div>
          <div className="bg-background/50 backdrop-blur-sm rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-primary">{stats.contactClicks}</div>
            <div className="text-xs text-muted-foreground">Contactos</div>
          </div>
        </div>

        <div className="bg-background/50 backdrop-blur-sm rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3 mb-3">
            <TrendingUp className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-sm mb-2">
                Con un Plan Premium, Multiplica Tus Oportunidades
              </h4>
              <ul className="space-y-1.5 text-xs text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                  <span><strong>Badge premium:</strong> Destaca como entrenador verificado</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                  <span><strong>Mayor visibilidad:</strong> Aparece primero en búsquedas</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                  <span><strong>Estadísticas avanzadas:</strong> Sigue cada interacción</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                  <span><strong>Video destacado:</strong> Muestra tu estilo de entrenamiento</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Button
            onClick={() => window.location.href = "/become-trainer"}
            className="w-full bg-gradient-primary hover:opacity-90"
          >
            <Crown className="w-4 h-4 mr-2" />
            Ver Planes Premium
          </Button>
          <Button
            onClick={handleDismiss}
            variant="ghost"
            className="w-full"
            size="sm"
          >
            Recordarme más tarde
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground mt-3">
          🚀 Los entrenadores premium reciben <strong className="text-primary">4x más contactos</strong>
        </p>
      </CardContent>
    </Card>
  );
};
